<template>
  <v-form ref="form" class="max-width pa-0">
    <ContinuousSurfaceWaterChart id="stationGraphChart" :requestData="requestData" />
    <v-divider />
    <v-card>
      <v-card-actions wrap class="card-bg-2 py-0 layout wrap">
        <v-btn class="my-2 ml-0 mr-2" color="info" @click="generateGraph()">
          <v-icon class="mr-1">insert_chart</v-icon>
          <div class="text-capitalize">{{ $t('Button.Generate_Graph') }}</div>
        </v-btn>
        <v-btn class="my-2 ml-0" :loading="loadingDownload" @click="download()">
          <v-icon class="mr-1">save_alt</v-icon>
          <div class="text-capitalize">{{ $t('Button.DownloadReport') }}</div>
        </v-btn>
      </v-card-actions>

      <v-progress-linear class="ma-0" indeterminate color="blue" v-show="loadingSpinner" />

      <div style="background-color: white;">
        <v-alert class="ma-0" outline transition="slide-y-transition" type="info" :value="showErrorBox && (alertShow || noSelectionWarning || invalidDatesWarning)">
          <div v-if="noSelectionWarning" class="primary--text">
            {{ $t('StationDetailDownload.alertDateSeries') }}
          </div>
          <div v-if="invalidDatesWarning" class="primary--text">
            {{ $t('StationDetailDownload.alertDateRange') }}
          </div>
          <div v-if="alertShow" class="primary--text">
            {{ $t('StationDetailDownload.alertStatisticalValue') }}
          </div>
        </v-alert>
      </div>

      <v-divider />

      <v-layout wrap class="card-bg-2">
        <v-flex xs12 md5 class="divider-right">
          <v-card-title class="headline pb-0">Date Range</v-card-title>
          <v-card-text class="pb-0">
            <DateRange v-model="dates" @update:selectedRange="selectedRange = $event" rounded solo :minDate="minDate" :maxDate="maxDate" :availableDateRange="{ start: minDate, end: maxDate }" :min-max-placeholder="formats.prettyDateFormat" :shortcutPrefix="'Show'" :shortcuts="shortcuts" no-arrows :data="availableDates" />
          </v-card-text>
          <v-divider />
          <v-card-title class="headline pb-0">
            Include gap markers
            <v-menu offset-y open-on-hover :open-delay="400">
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" class="clickable ml-2" color="primary">
                info_outline
              </v-icon>
            </template>
            <v-card>
              <v-card-text>
                "Include gap markers" inserts a row mid-way between the start and end of a gap, and applies a missing (M) grade for that row.
              </v-card-text>
            </v-card>
          </v-menu>
          </v-card-title>
          <v-card-text class="pb-0">
            <v-radio-group v-model="includeGapMarkers" row>
              <v-radio color="info" hide-details :label="'No'" :value="false"></v-radio>
              <v-radio color="info" hide-details :label="'Yes'" :value="true"></v-radio>
            </v-radio-group>
          </v-card-text>
          <v-divider />
          
          <v-card-title class="headline">
            Download Options

          </v-card-title>
          <v-card-text class="py-0">
            <v-radio-group v-model="downloadOption" row>
              <v-radio v-for="(downloadType, index) in downloadTypes" color="info" hide-details :key="`downloadType-${index}`" :label="downloadType.name" :value="downloadType"></v-radio>
            </v-radio-group>
          </v-card-text>

          <template v-if="downloadOption == downloadTypes.statistic">
            <div class="mb-2">
              <SecondaryBtn @click="toggleAdvancedOptions">
                <v-icon small left>{{ dailyStatisticsAdvancedOptionIcon }}</v-icon>
                Advanced Options
              </SecondaryBtn>
            </div>
            <v-expand-transition>
              <v-card-text class="card-bg-1 mb-2" v-show="showAdvancedOptions" transition="slide-y-transition">
                <DailyStatisticTimeOffset v-model="hourOffset" :startDate="dates.start" :endDate="dates.end" />
              </v-card-text>
            </v-expand-transition>
            <v-data-table :pagination="{ rowsPerPage: -1 }" :headers="dialogHeaders" :items="shownStatistics" class="elevation-1">
              <template v-slot:no-data>
                <div class="pa-4 text-xs-center">
                  <v-icon color="warning">warning</v-icon>
                  <br />
                  Select a time series
                </div>
              </template>
              <template v-slot:headers>
                <tr>
                  <!-- Padding is height of the checkboxes row -->
                  <th style="padding-bottom: 24px;" class="text-xs-left time-series-column">Data Series Name</th>
                  <th class="py-3">
                    <div class="text-xs-left">Daily Statistics</div>
                    <v-layout>
                      <v-checkbox v-for="(statisticType, index) in statisticTypes" color="primary" :key="`statisticTotals-${index}`" class="mr-1" :style="{ 'order': statisticType.order }" @click.stop.prevent="toggleStatisticType(statisticType)" :label="statisticType.name" :indeterminate="selectedStatistics[statisticType.key].hasSome && !selectedStatistics[statisticType.key].hasAll" :input-value="selectedStatistics[statisticType.key].hasAll" :disabled="shownStatistics.length == 0 || (statisticType == statisticTypes.total && !shownStatistics.some(item => item.Total))" hide-details />
                    </v-layout>
                  </th>
                </tr>
              </template>
              <template v-slot:items="{ item, index }">
                <td class="time-series-column">{{ item.DisplayName }}</td>
                <td>
                  <v-layout>
                    <v-checkbox :style="{ 'order': statisticType.order }" color="info" v-for="(statisticType, statIndex) in statisticTypes" :key="`${index}-statisticType-${statIndex}`" class="mr-1" v-model="item.Statistics" :value="statisticType.value" :rules="[rules.hasOne]" :disabled="statisticType == statisticTypes.total && !item.Total" hide-details>
                      <template v-slot:label>
                        <div class="visibility-hidden">
                          {{ statisticType.name }}
                        </div>
                      </template>
                    </v-checkbox>
                  </v-layout>
                </td>
              </template>
            </v-data-table>
            <v-alert class="ma-0 py-2" outline transition="slide-y-transition" type="warning" style="background: white;" :value="shownStatistics.some(item => !item.Total)">
              <div class="primary--text">
                {{ $t('StationDetailDownload.alertDailyTotal') }}
              </div>
            </v-alert>

            <v-card-title class="headline pb-0">
            Fill Gaps
            <v-menu offset-y open-on-hover :open-delay="400">
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" class="clickable ml-2" color="primary">
                info_outline
              </v-icon>
            </template>
            <v-card>
              <v-card-text>
                "Fill Gaps" inserts a row of date without data, and applies a missing (M) grade for that row.
              </v-card-text>
            </v-card>
          </v-menu>
          </v-card-title>
          <v-card-text class="pb-0">
            <v-radio-group v-model="fillGaps" row>
              <v-radio color="info" hide-details :label="'No'" :value="false"></v-radio>
              <v-radio color="info" hide-details :label="'Yes'" :value="true"></v-radio>
            </v-radio-group>
          </v-card-text>
          <v-divider />
          
          </template>
        </v-flex>

        <v-flex xs12 md7 class="divider-left">
          <v-card-title>
            <div class="headline pb-0" style="align-items: baseline; display: flex;">
              Time series
            </div>
            <v-spacer />
            <v-text-field @click:clear="clearSearch()" prepend-inner-icon="search" v-model="search" @keyup.enter="toggleCurrentItems($event)" class="rounded font-weight-regular" hide-details clearable solo placeholder="Search time series" />
          </v-card-title>

          <v-item-group mandatory v-model="filterToggle" active-class="white--text">
            <v-subheader style="display: inline; cursor: default;">Show:</v-subheader>
            <v-item>
              <v-chip small slot-scope="{ active, toggle }" :color="active ? 'primary' : 'default'" :selected="active" class="chipFilter" @click="toggle">All</v-chip>
            </v-item>
            <v-item>
              <v-chip small slot-scope="{ active, toggle }" :color="active ? 'primary' : 'default'" :selected="active" class="chipFilter" @click="toggle">
                <v-avatar class="chip-log-count" style="border-radius: 0px;">{{ selected.length }}</v-avatar>
                Selected
              </v-chip>
            </v-item>
          </v-item-group>

          <v-divider />
          <v-data-table ref="timeSeriesTable" v-model="selected" :items="datasets" :headers="headers" :search="search" class="table-default-bg" select-all :pagination.sync="pagination" :item-key="ID_KEY" :custom-filter="customFilter" :class="{ 'five-items': pagination.rowsPerPage === 5, 'ten-items': pagination.rowsPerPage === 10, 'twenty-five-items': pagination.rowsPerPage === 25 }">
            <template v-slot:headers="props">
              <tr>
                <th>
                  <v-checkbox color="primary" :input-value="props.all" :indeterminate="props.indeterminate" primary hide-details @click.stop="toggleAll()"></v-checkbox>
                </th>
                <th v-for="header in props.headers" :key="header.text" :class="['column sortable', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '']" @click="changeSort(header.value)">
                  <v-icon small>arrow_upward</v-icon>
                  {{ header.text }}
                </th>
              </tr>
            </template>
            <template v-slot:items="props">
              <tr class="clickable" :active="props.selected" @click="props.selected = !props.selected">
                <td>
                  <v-checkbox color="info" primary hide-details :input-value="props.selected" />
                </td>
                <td>{{ props.item.DisplayName }}</td>
                <td class="text-xs-left">
                  {{ formatDate(props.item.FirstRecordDate, formats.prettyDateTimeFormatShort) }}
                </td>
                <td class="text-xs-left">
                  {{ formatDate(props.item.LastRecordDate, formats.prettyDateTimeFormatShort) }}
                </td>
                <td class="text-xs-left">
                  <v-chip v-for="tag in props.item.Tags" :key="tag" small disabled text-color="black">{{tag}}</v-chip>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>
    </v-card>
    <v-dialog v-model="confirming" persistent
      max-width="640">
      <v-card>
        <v-card-title>
          <h5>There is a problem with the name of the time series "{{ invalidTimeSeries.join() }}" you are trying to calculate daily statistics on. Daily statistics will be calculated but partial grades ('A') may not be properly assigned. Please contact 'envirodata-techsupport@hatfieldgroup.com' to have this issue addressed.</h5>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="continueDownload">Download anyway</v-btn>
          <v-btn color="error" @click="cancelDownload">{{ $t('Button.No') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-form>  
</template>

<script>
  import {
    createNamespacedHelpers,
  } from 'vuex'
  const monitoringFeatures = createNamespacedHelpers('monitoringfeature')
  const defaultHourOffset = 0
  import DataTable from '@/components/mixins/DataTable'
  import DownloadUtil from '@/components/mixins/DownloadUtil'
  import DailyStatisticTimeOffset from '@/components/MonitoringFeature/DataSources/ContinuousAquariusData/Tabs/GraphSubComponents/DailyStatisticTimeOffset'

  import ContinuousSurfaceWaterChart from './ContinuousSurfaceWaterChart'

  import {
    SOURCE_TYPES,
  } from '@/store/MonitoringFeature/monitoringfeature'

  import {
    formatDate,
    prettyDateFormat,
    backendDateTimeFormat,
    prettyDateTimeFormatShort,
  } from '@/components/mixins/MomentUtil'

  import {
    customSearcher,
    getObjectValues,
  } from '@/helpers/search'

  import {
    mergeDistinctObjectArrays,
  } from '@/helpers/arrays'

  import {
    min,
    max,
    startOfDay,
    endOfDay,
  } from 'date-fns'

  import moment from 'moment'

  import {
    statisticTypes,
  } from '@/helpers/timeSeries'

  const downloadTypes = {
    download: {
      name: 'Raw data',
    },
    statistic: {
      name: 'Daily statistics',
    },
  }

  const filterToggles = {
    all: 0,
    selected: 1,
  }

  const ID_KEY = 'AquariusId'
  const SEARCH_KEY = 'DisplayName'
  const RAW_DATA_FILE_PREFIX = 'StationContinuousDataExport'
  const STATISTIC_DATA_FILE_PREFIX = 'StationContinuousStatsExport'

  export default {
    components: {
      ContinuousSurfaceWaterChart,
      DailyStatisticTimeOffset,
    },
    mixins: [DataTable, DownloadUtil],
    data() {
      return {
        ID_KEY,
        filter: {
          items: [],
        },
        itemsMetadata: {},
        statisticTypes,
        downloadTypes,
        includeGapMarkers: false,
        fillGaps: false,
        filterToggles,
        filterToggle: 0,
        selectedRange: {
          key: 'pastThreeMonthsWithData',
        },
        selected: [],
        search: '',
        dates: {
          start: null,
          end: null,
        },
        pagination: {
          sortBy: 'name',
          rowsPerPage: 10,
          page: 1,
        },
        formats: {
          prettyDateFormat,
          prettyDateTimeFormatShort,
        },
        headers: [{
          text: 'Data Series Name',
          align: 'left',
          value: 'DisplayName',
        },
        {
          text: 'First Record',
          value: 'FirstRecordDate',
        },
        {
          text: 'Last Record',
          value: 'LastRecordDate',
        },
        {
          text: 'Tag',
          value: 'Tag',
        },
        ],
        rules: {
          hasOne: (v) => v && v.length > 0 || 'Select at least one option',
        },
        availableDates: [],
        selectedDatasets: null,
        showErrorBox: false,
        requestData: null,
        minDate: null, // Date object
        maxDate: null, // Date object
        hourOffset: defaultHourOffset,
        statistics: [],
        loadingSpinner: false,
        downloadDialog: false,
        alertShow: false,
        downloadOption: downloadTypes.download,
        dialogHeader: [{
          text: 'Data Series Name',
          value: '',
        }],
        dialogHeaders: [{
          text: 'Data Series Name',
          value: '',
          align: 'left',
          sortable: false,
        },
        {
          text: 'Daily statistics',
          value: '',
          align: 'left',
          sortable: false,
        },
        ],
        keywords: ['Precipitation', 'Rainfall', 'Evapotranspiration'],
        lockwords: ['Cumulative', 'cumul'],
        loadingDownload: false,
        showAdvancedOptions: false,
        confirming: false,
        invalidTimeSeries: [],
      }
    },

    computed: {
      ...monitoringFeatures.mapGetters([
        'currentFeatureDataSourceIndexByType',
        'currentFeatureDataSourceData',
      ]),
      continuousData() {
        return this.currentFeatureDataSourceData(SOURCE_TYPES.continuousAquarius)
      },
      datasets() {
        return (this.continuousData && this.continuousData.details && this.continuousData.details.Datasets) || []
      },
      customSearch() {
        return customSearcher(this.itemsMetadata, ID_KEY, this.filter)
      },
      selectedStatistics() {
        let result = {}

        Object.entries(this.statisticTypes).forEach(([key, {
          value,
        }]) => {
          let count = this.shownStatistics.filter(item => item.Statistics.includes(value)).length

          result[key] = {
            hasSome: count > 0,
            hasAll: count > 0 && count == this.shownStatistics.length,
          }
        })

        return result
      },
      shortcuts() {
        return {
          all: {
            name: () => 'All available data',
            make: () => {
              return {
                start: this.minDate ? moment(this.minDate) : null,
                end: this.maxDate ? moment(this.maxDate) : null,
              }
            },
          },
          pastMonthWithData: {
            key: 'pastMonthWithData',
            index: 1,
            name: () => 'Past month with available data',
            make: () => {
              return {
                start: moment(this.maxDate).clone().add(-1, 'months'),
                end: moment(this.maxDate),
              }
            },
            isMatch: (start, end, availableDates) => {
              if (!start || !end) return false
              return (end.clone().add(-1, 'months').isSame(start, 'days') && availableDates.end.isSame(end, 'days'))
            },
          },
          pastTwoWeekWithData: {
            key: 'pastTowWeekWithData',
            index: 2,
            name: () => 'Past two weeks with available data',
            make: () => {
              return {
                start: moment(this.maxDate).clone().add(-14, 'days'),
                end: moment(this.maxDate),
              }
            },
            isMatch: (start, end, availableDates) => {
              if (!start || !end) return false
              return (end.clone().add(-14, 'days').isSame(start, 'days') && availableDates.end.isSame(end, 'days'))
            },
          },
          pastThreeMonthsWithData: {
            key: 'pastThreeMonthsWithData',
            index: 3,
            name: () => 'Past three months with available data',
            make: () => {
              return {
                start: moment(this.maxDate).clone().add(-3, 'months'),
                end: moment(this.maxDate),
              }
            },
            isMatch: (start, end, availableDates) => {
              if (!start || !end) return false
              return (end.clone().add(-3, 'months').isSame(start, 'days') && availableDates.end.isSame(end, 'days'))
            },
          },
        }
      },
      noSelectionWarning() {
        return (!this.selected.length)
      },
      invalidDatesWarning() {
        return !(this.dates.start && this.dates.end)
      },
      shownStatistics() {
        return this.statistics.filter(statistic => statistic.show)
      },
      dailyStatisticsAdvancedOptionIcon() {
        return this.showAdvancedOptions ? 'arrow_drop_down' : 'arrow_right'
      },
    },
    watch: {
      datasets: {
        immediate: true,
        handler(value) {
          this.makeItemsMetadata(value)
          this.update()
          this.statistics = value.map(x => ({
            show: false,
            DisplayName: x.DisplayName,
            DataSetIdentifer: x.DataSetIdentifer,
            Statistics: [],
            Total: this.keywords.some((e) => x.DisplayName.indexOf(e) >= 0) &&
              this.lockwords.every(e => x.DisplayName.indexOf(e) < 0),
          }))
        },
      },
      filterToggle(value) {
        if (value == filterToggles.selected) {
          this.pagination.page = 1
        }
      },
      selected(items) {
        this.update()

        this.statistics.forEach(statistic => {
          statistic.show = !!items.find(item => item.DisplayName == statistic.DisplayName)
        })
      },
    },
    methods: {
      formatDate,
      clearSearch() {
        this.$nextTick(() => this.search = '')
      },
      customFilter(items, index) {
        let searchResult = this.customSearch(items, index)
        let result = []

        if (this.filterToggle == filterToggles.selected) {
          searchResult.forEach(item => {
            let findIndex = this.selected.findIndex(s => s[ID_KEY] === item[ID_KEY])
            if (findIndex > -1) {
              result.push(item)
            }
          })
        } else {
          result = searchResult
        }

        return result
      },
      makeItemsMetadata(items) {
        let result = {}
        items.forEach(item => {
          result[item[ID_KEY]] = getObjectValues(item, SEARCH_KEY)
        })

        this.itemsMetadata = result
      },
      update() {
        this.loadingSpinner = false

        let startDates
        let endDates

        let selected = this.selected
        if (selected && selected.length > 0) {
          startDates = this.selected.map(d => d.FirstRecordDate)
          endDates = this.selected.map(d => d.LastRecordDate)
        } else {
          startDates = this.datasets.map(d => d.FirstRecordDate)
          endDates = this.datasets.map(d => d.LastRecordDate)
        }

        this.availableDates = startDates.concat(endDates)
        this.minDate = min(...startDates)
        this.maxDate = max(...endDates)        
        if (this.selectedRange && this.selectedRange.key == 'pastMonthWithData') {
          this.dates = {
            start: moment(this.maxDate).clone().add(-1, 'months'),
            end: this.maxDate,
          }
        }
        if (this.selectedRange && this.selectedRange.key == 'pastTwoWeekWithData') {
          this.dates = {
            start: moment(this.maxDate).clone().add(-14, 'days'),
            end: this.maxDate,
          }
        }
        if (this.selectedRange && this.selectedRange.key == 'pastThreeMonthsWithData') {
          this.dates = {
            start: moment(this.maxDate).clone().add(-3, 'months'),
            end: this.maxDate,
          }
        }
        if (this.selectedRange.key == 'all') {
          this.dates = {
            start: this.minDate,
            end: this.maxDate,
          }
        }
      },
      toggleCurrentItems() {
        let filteredItems = this.$refs.timeSeriesTable.filteredItems
        this.selected = mergeDistinctObjectArrays(this.selected, filteredItems, SEARCH_KEY)
      },
      toggleAll() {
        let selected = {}

        let filteredItems = this.filter.items
        this.selected.forEach(item => {
          selected[item[ID_KEY]] = item
        })

        if (filteredItems.every(item => selected[item[ID_KEY]])) {
          filteredItems.forEach(item => {
            delete selected[item[ID_KEY]]
          })
        } else {
          filteredItems.forEach(item => {
            selected[item[ID_KEY]] = item
          })
        }

        this.selected = Object.values(selected)
      },
      toggleStatisticType(statisticType) {
        if (this.shownStatistics.length == 0) return
        const indexOf = (item, type) => item.Statistics.indexOf(type.value)

        if (this.selectedStatistics[statisticType.key].hasAll) {
          this.shownStatistics.forEach(item => {
            let index = indexOf(item, statisticType)
            if (index !== -1) item.Statistics.splice(index, 1)
          })
        } else {
          this.shownStatistics.forEach(item => {
            if (statisticType == statisticTypes.total && !item.Total) return
            let index = indexOf(item, statisticType)
            if (index === -1) item.Statistics.push(statisticType.value)
          })
        }
      },
      toggleAdvancedOptions() {
        this.showAdvancedOptions = !this.showAdvancedOptions
      },
      generateGraph() {
        let hasRequiredInputs = !(this.noSelectionWarning || this.invalidDatesWarning)
        if (hasRequiredInputs) {
          this.selectedDatasets = this.selected
          this.requestData = {
            datasets: this.selectedDatasets,
            startDate: startOfDay(this.dates.start),
            endDate: endOfDay(this.dates.end),
            IncludeGapMarkers: this.includeGapMarkers,
          }
          this.showErrorBox = false
        } else {
          this.showErrorBox = true
        }

      },
      download() {
        if (!this.$refs.form.validate()) return

        let hasRequiredInputs = !(this.noSelectionWarning || this.invalidDatesWarning)

        if(hasRequiredInputs) {
          this.showErrorBox = false
          if(!this.validateSelectedTimeSeries() && this.downloadOption == this.downloadTypes.statistic) {
            this.confirming = true
            return
          }
          this.downloadReport()
        } else {
          this.showErrorBox = true
        }

        
      },
      downloadReport() {
        if (this.downloadOption == this.downloadTypes.download) {
          this.downloadRawData()
        } else if (this.downloadOption == this.downloadTypes.statistic) {
          this.downloadStatisticData()
        }
      },
      formatRawDataFileName(dataSetIdentifierList, startMoment, endMoment) {
        return this.formatExportFileName(RAW_DATA_FILE_PREFIX, dataSetIdentifierList, startMoment, endMoment)
      },
      formatStatisticDataFileName(dataSetIdentifierList, startMoment, endMoment) {
        return this.formatExportFileName(STATISTIC_DATA_FILE_PREFIX, dataSetIdentifierList, startMoment, endMoment)
      },
      formatExportFileName(fileNamePrefix, dataSetIdentifierList, startMoment, endMoment) {
        const paramList = this.extractUniqueParameters(dataSetIdentifierList)
        const stationList = this.extractUniqueStationIds(dataSetIdentifierList)
        const paramFileNamePart = paramList.join(',')
        const startDateTerm = startMoment.format(prettyDateFormat)
        const endDateTerm = endMoment.format(prettyDateFormat)
        return `${fileNamePrefix}_${stationList}_${paramFileNamePart}_${startDateTerm}_${endDateTerm}.xlsx`
      },
      extractUniqueParameters(dataSetIdentifierList) {
        if (dataSetIdentifierList == null) {
          return []
        }
        const uniqueParamDict = dataSetIdentifierList.reduce((acc, x) => {
          const param = x.split('.')[0]
          acc[param] = true
          return acc
        }, {})
        return Object.keys(uniqueParamDict)
      },
      extractUniqueStationIds(dataSetIdentifierList) {
        // receives an array of strings where each string contains a station
        // identifier terminated by an '@' (e.g. "param@stationId").
        // we want the unique stationId values (the portion after the '@').
        if (dataSetIdentifierList == null) {
          return []
        }
        const uniqueStationDict = dataSetIdentifierList.reduce((acc, x) => {
          // guard in case the format isn't exactly as expected
          const parts = x.split('@')
          const station = parts.length > 1 ? parts[1] : ''
          if (station) {
            acc[station] = true
          }
          return acc
        }, {})
        return Object.keys(uniqueStationDict)
      },
      downloadRawData() {
        const startMoment = moment(this.dates.start)
        const endMoment = moment(this.dates.end)
        let requestData = {
          StartDateTime: startMoment.format(backendDateTimeFormat),
          EndDateTime: endMoment.format(backendDateTimeFormat),
          Offset: 0,
          Mode: 'Raw',
          IncludeGapMarkers: this.includeGapMarkers,
          FillGaps: this.fillGaps,
          TimeSeries: this.shownStatistics.map(x => {
            return {
              TimeSeriesIdentifier: x.DataSetIdentifer,
              Statistics: ['Raw'],
            }
          }),
        }

        let url = '/Report/ContinuousStation'

        let hasRequiredInputs = !(this.noSelectionWarning || this.invalidDatesWarning)
        if (hasRequiredInputs) {
          this.loadingDownload = true
          let timeSeriesIdentifiers = this.selected.map(x => x.DataSetIdentifer)
          let fileName = this.formatRawDataFileName(timeSeriesIdentifiers, startMoment, endMoment)
          let options = {
            successful: () => {
              this.downloadDialog = false
              this.loadingDownload = false
            },
            failedCb: (err) => {
              this.loadingDownload = false
              this.$store.dispatch('app/setSnackbar', {
                text: err.response.data,
                color: 'error',
                icon: 'error',
              })
            },
          }
          this.downloadByPost(url, requestData, fileName, options)
          this.showErrorBox = false
        } else {
          this.showErrorBox = true
        }
      },
      downloadStatisticData() {
        if (this.shownStatistics.some(statistic => statistic.length == 0)) {
          this.alertShow = true
        } else {
          this.alertShow = false
        }

        if (!this.alertShow) {
          const startMoment = moment(this.dates.start)
          const endMoment = moment(this.dates.end)
          let requestData = {
            StartDateTime: this.dates.start,
            EndDateTime: this.dates.end,
            Offset: this.hourOffset || 0,
            Mode: 'Daily',
            IncludeGapMarkers: this.includeGapMarkers,
            FillGaps: this.fillGaps,
            TimeSeries: this.shownStatistics.map(x => {
              return {
                TimeSeriesIdentifier: x.DataSetIdentifer,
                Statistics: x.Statistics,
              }
            }),
          }
          let url = '/Report/ContinuousStation'

          let hasRequiredInputs = !(this.noSelectionWarning || this.invalidDatesWarning)
          if (hasRequiredInputs) {
            this.loadingDownload = true
            let timeSeriesIdentifiers = this.selected.map(x => x.DataSetIdentifer)
            let fileName = this.formatStatisticDataFileName(timeSeriesIdentifiers, startMoment, endMoment)
            let options = {
              successful: () => {
                this.downloadDialog = false
                this.loadingDownload = false
              },
              failedCb: (err) => {
                this.loadingDownload = false
                this.$store.dispatch('app/setSnackbar', {
                  text: err.response.data,
                  color: 'error',
                  icon: 'error',
                })
              },
            }
            this.downloadByPost(url, requestData, fileName, options)
            this.showErrorBox = false
          } else {
            this.showErrorBox = true
          }
        }
      },
      validateSelectedTimeSeries() {
        // Reset previous results
        this.invalidTimeSeries = []

        const supportedIntervals = ['2 sec', '2sec', '10 sec', '10 seconds', '1 minute', '5 minute', '15 minute', '30 minute', 'hourly', 'two hour',
          'four hour', '4 hour', 'six hour', '6 hour', '12 hour', 'daily', 'monthly']

        const normalize = (s) => {
          return s.toLowerCase().trim()
            .replace(/[^a-z0-9\s]/g, '')
            .replace(/\s+/g, ' ')
            .split(' ')
            .map(w => (w.length > 1 && w.endsWith('s')) ? w.slice(0, -1) : w)
            .join(' ')
        }

        const supportedNormalized = supportedIntervals.map(normalize)

        this.selected.forEach(x => {
          if (x.DataSetIdentifer.indexOf('.Field Visits') < 0) {
            const startIndex = x.DataSetIdentifer.lastIndexOf('-')
            const endIndex = x.DataSetIdentifer.lastIndexOf('@')
            let intervalRaw = ''

            if (startIndex === -1 || endIndex === -1 || endIndex <= startIndex) {
              // fallback: try to capture text between the last '-' and the next '@'
              const m = x.DataSetIdentifer.match(/-\s*([^@]+)@/)
              if (m && m[1]) {
                intervalRaw = m[1].trim()
              } else {
                this.invalidTimeSeries.push(x.DisplayName)
                return
              }
            } else {
              // be permissive about spacing after the hyphen
              intervalRaw = x.DataSetIdentifer.substring(startIndex + 1, endIndex).trim()
            }

            const intervalNorm = normalize(intervalRaw)

            // consider a match if either side contains the other (covers minor plural/typo differences)
            const matched = supportedNormalized.some(si => intervalNorm.includes(si) || si.includes(intervalNorm))

            if (!matched) {
              this.invalidTimeSeries.push(x.DisplayName)
            }
          }
        })

        return !(this.invalidTimeSeries.length > 0)
      },
      continueDownload() {
        this.invalidTimeSeries = []
        this.confirming =false

        this.downloadReport()
      },
      cancelDownload() {
        this.invalidTimeSeries = []
        this.confirming =false
      },
    },
  }
</script>

<style scoped>
  .time-series-column {
    width: 165px;
    word-wrap: break-word;
  }
</style>